import React, { useMemo, useRef, useEffect } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const TranscriptCrawlWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const scrollRef = useRef<HTMLDivElement>(null);

  // Last 20 entries for the crawl
  const recentEntries = useMemo(() => {
    const transcript = state?.transcript ?? [];
    return transcript.slice(-20);
  }, [state?.transcript]);

  // Restart animation when new entries arrive
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      // Reset animation
      el.style.animation = 'none';
      el.offsetHeight; // trigger reflow
      el.style.animation = '';
    }
  }, [recentEntries.length]);

  if (!state) return null;

  const sessionAvatars = state?.sessionAvatars ?? [];

  const getAvatarColor = (role?: string) => {
    const avatar = sessionAvatars.find(a => a.role === role);
    return avatar?.primaryColor || '#00c7c4';
  };

  const getAvatarName = (role?: string) => {
    if (!role) return 'Facilitator';
    const avatar = sessionAvatars.find(a => a.role === role);
    return avatar?.name || role;
  };

  if (recentEntries.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        Waiting for transcript...
      </div>
    );
  }

  // Build the crawl text
  const crawlText = recentEntries.map((entry, i) => (
    <span key={`${entry.speaker}-${i}`} className="inline-flex items-center mx-6 shrink-0">
      <span
        className="font-bold text-xs uppercase mr-2"
        style={{ color: entry.speaker === 'avatar' ? getAvatarColor(entry.avatarRole) : isDark ? '#94a3b8' : '#64748b' }}
      >
        {entry.speaker === 'avatar' ? getAvatarName(entry.avatarRole) : 'Facilitator'}
      </span>
      <span className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
        {entry.text.length > 120 ? entry.text.slice(0, 120) + '...' : entry.text}
      </span>
      <span className={`mx-4 ${isDark ? 'text-white/10' : 'text-slate-200'}`}>•</span>
    </span>
  ));

  return (
    <div className="relative h-full overflow-hidden flex items-center">
      {/* Gradient fades on edges */}
      <div className={`absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none ${
        isDark
          ? 'bg-gradient-to-r from-[#0b1120] to-transparent'
          : 'bg-gradient-to-r from-white to-transparent'
      }`} />
      <div className={`absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none ${
        isDark
          ? 'bg-gradient-to-l from-[#0b1120] to-transparent'
          : 'bg-gradient-to-l from-white to-transparent'
      }`} />

      {/* Scrolling content */}
      <div
        ref={scrollRef}
        className="flex whitespace-nowrap animate-crawl"
        style={{
          animationDuration: `${Math.max(20, recentEntries.length * 8)}s`,
        }}
      >
        {/* Duplicate content for seamless loop */}
        {crawlText}
        {crawlText}
      </div>

      <style>{`
        @keyframes crawl {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-crawl {
          animation: crawl linear infinite;
        }
      `}</style>
    </div>
  );
};

export default TranscriptCrawlWidget;
